import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

export default function PatentDetailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const patentId = searchParams.get('id');
  const returnUrl = searchParams.get('return');
  
  const [patent, setPatent] = useState(null);
  const [patentImage, setPatentImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (!patentId) {
      setIsLoading(false);
      return;
    }

    const fetchPatentDetail = async () => {
      setIsLoading(true);
      setError('');
      setCurrentImageIndex(0);

      try {
        const { patentService } = await import('../../services/patentService');
        
        const data = await patentService.getPatentDetail(patentId);
        setPatent(data);
        
        const imageUrl = await patentService.getPatentImage(patentId);
        setPatentImage(imageUrl);
      } catch (err) {
        setError(err.message || 'Failed to fetch patent details. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPatentDetail();
  }, [patentId]);

  useEffect(() => {
    if (!patent || !patent.image_urls || patent.image_urls.length <= 1) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        setCurrentImageIndex((prev) => 
          prev === 0 ? patent.image_urls.length - 1 : prev - 1
        );
      } else if (e.key === 'ArrowRight') {
        setCurrentImageIndex((prev) => 
          prev === patent.image_urls.length - 1 ? 0 : prev + 1
        );
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [patent]);

  useEffect(() => {
    const container = document.querySelector('.patent-image-container');
    if (!container) return;
    
    const img = container.querySelector('img');
    const placeholder = container.querySelector('.image-error-placeholder');
    
    if (img) {
      img.style.display = '';
    }
    if (placeholder) {
      placeholder.style.display = 'none';
    }
  }, [currentImageIndex]);

  const handleBack = () => {
    if (returnUrl) {
      navigate(returnUrl);
    } else {
      navigate(-1);
    }
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) {
      return;
    }
    navigate(`/patent?id=${encodeURIComponent(searchQuery.trim())}`);
  };

  if (!patentId) {
    return (
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-2xl space-y-6">
          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold text-white">Patent Detail Lookup</h1>
            <p className="text-lg text-gray-300">
              Enter a patent ID to view detailed patent information
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-xl p-8 space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-200">
                Patent ID
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="e.g., US10123456B2"
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-400">
                Enter the patent number to view its details
              </p>
            </div>

            <button
              onClick={handleSearch}
              disabled={!searchQuery.trim()}
              className="w-full py-4 px-6 rounded-xl font-bold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 shadow-lg transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              View Patent Details
            </button>
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate('/')}
              className="text-gray-300 hover:text-white transition-all text-sm"
            >
              Or go back to search page
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-4">
          <svg className="animate-spin h-12 w-12 text-white mx-auto" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-xl text-white font-semibold">Loading patent details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-red-500/90 text-white rounded-2xl p-8 space-y-4 text-center">
          <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-lg font-semibold">{error}</p>
          <button
            onClick={handleBack}
            className="px-6 py-2 bg-white text-red-600 rounded-xl font-semibold hover:bg-gray-100 transition-all"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!patent) {
    return null;
  }

  return (
    <div className="flex-1 px-4 py-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Results
          </button>
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
          <div className="flex items-start gap-2 mb-4">
            <span className="inline-block px-3 py-1 bg-purple-600 text-white text-sm font-semibold rounded-lg">
              {patent.patent_id || patentId}
            </span>
          </div>

          <h1 className="text-3xl font-bold text-white mb-6">
            {patent.title || 'Patent Title'}
          </h1>

          <div className={`grid grid-cols-1 ${patent.image_urls && patent.image_urls.length > 0 ? 'lg:grid-cols-2' : ''} gap-6`}>
            {patent.image_urls && patent.image_urls.length > 0 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-white mb-3">Patent Images</h2>
                  <div className="bg-gray-900 rounded-xl overflow-hidden">
                    <div className="patent-image-container relative aspect-[4/3] bg-gray-800 flex items-center justify-center">
                      <img
                        src={patent.image_urls[currentImageIndex]}
                        alt={`Patent figure ${currentImageIndex + 1}`}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          if (patentImage && e.target.src !== patentImage) {
                            e.target.src = patentImage;
                          } else {
                            e.target.style.display = 'none';
                            const placeholder = e.target.parentElement.querySelector('.image-error-placeholder');
                            if (placeholder) placeholder.style.display = 'flex';
                          }
                        }}
                      />
                      <div className="image-error-placeholder hidden w-full h-full items-center justify-center absolute inset-0">
                        <div className="text-center text-gray-400">
                          <svg className="w-16 h-16 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-sm">No image available</p>
                        </div>
                      </div>
                      
                      {patent.image_urls.length > 1 && (
                        <>
                          <button
                            onClick={() => setCurrentImageIndex((prev) => 
                              prev === 0 ? patent.image_urls.length - 1 : prev - 1
                            )}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
                            aria-label="Previous image"
                          >
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                          
                          <button
                            onClick={() => setCurrentImageIndex((prev) => 
                              prev === patent.image_urls.length - 1 ? 0 : prev + 1
                            )}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
                            aria-label="Next image"
                          >
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          
                          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 px-3 py-1 rounded-full">
                            <span className="text-white text-sm font-medium">
                              {currentImageIndex + 1} / {patent.image_urls.length}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {patent.image_urls.length > 1 && (
                      <div className="p-3 bg-gray-800/50">
                        <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
                          {patent.image_urls.map((url, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentImageIndex(index)}
                              className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                                currentImageIndex === index
                                  ? 'border-purple-500 ring-2 ring-purple-500/50'
                                  : 'border-gray-600 hover:border-gray-400'
                              }`}
                            >
                              <img
                                src={url}
                                alt={`Thumbnail ${index + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  if (patentImage && e.target.src !== patentImage) {
                                    e.target.src = patentImage;
                                  } else {
                                    e.target.style.display = 'none';
                                  }
                                }}
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {patent.metadata && (
                  <div className="bg-gray-800/50 rounded-xl p-4">
                    <h2 className="text-white font-semibold mb-3">Metadata</h2>
                    <div className="space-y-2 text-sm">
                      {Object.entries(patent.metadata).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-gray-400">{key}:</span>
                          <span className="text-white font-medium">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className={`space-y-6 ${!patent.image_urls || patent.image_urls.length === 0 ? 'lg:col-span-1' : ''}`}>
              <div>
                <h2 className="text-xl font-semibold text-white mb-3">Abstract</h2>
                <p className="text-gray-300 leading-relaxed whitespace-pre-line">
                  {patent.abstract || 'No abstract available.'}
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-white mb-3">Independent Claim</h2>
                <p className="text-gray-300 leading-relaxed whitespace-pre-line">
                  {patent.independent_claim || 'No claims available.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

